import { AvatarModel, DataSourceModel, EmployeeBasicModel, EmployeeDetailsModel, EmployeeModel, ExternalEmployeeExtendedModel, ExternalWorkLocationModel, LoginDataModel, ParticipantModel, TenantModel } from "@shared/models";
import { AssignmentBasicConfirmed } from "@shared/factories";
import { FormatDatesService } from "@shared/services";

export class EmployeeBasic {
  id:              number;
  first_name:      string;
  last_name:       string;
  personal_number: number;
  constructor(data: EmployeeBasicModel) {
    this.id              = data.id;
    this.first_name      = data.first_name;
    this.last_name       = data.last_name;
    this.personal_number = data.personal_number;
  }

  get name(): string {
    return `${this.first_name} ${this.last_name}`;
  }

  get nameReverse(): string {
    return `${this.last_name}, ${this.first_name}`;
  }

  toJSON(): EmployeeBasicModel {
    return {
      id:              this.id,
      first_name:      this.first_name,
      last_name:       this.last_name,
      personal_number: this.personal_number,
    };
  }
}

export class Employee extends EmployeeBasic {
  email:           string;
  phone_number:    string;
  userGroup:       string;
  constructor(data: EmployeeModel) {
    super(data);
    this.email        = data.email;
    this.phone_number = data.phone_number;
    this.userGroup    = data.user_group;
  }
  
  toJSON(): EmployeeModel {
    return Object.assign(super.toJSON(), {
      email:           this.email,
      phone_number:    this.phone_number,
      user_group:      this.userGroup
    });
  }
}

export class EmployeeDetails extends EmployeeBasic {
  createdAt:           Date;
  activatedAt:         Date;
  lastLoginAt:         Date;

  entryDate:           Date;
  exitDate:            Date;

  avatar:              AvatarModel;
  avatarImage:         string;

  lastConnection:      LoginData;

  tenant:              TenantModel;
  phoneNumber:         string;
  workLocation:        ExternalWorkLocationModel;
  dataSources:         DataSourceModel[];
  altPersonalAccounts: number[];
  constructor(data: EmployeeDetailsModel) {
    super(data);

    this.createdAt           = FormatDatesService.parseDate(data.created_at);
    this.activatedAt         = FormatDatesService.parseDate(data.activated_at);
    this.lastLoginAt         = FormatDatesService.parseDate(data.last_login_at);

    this.entryDate           = FormatDatesService.parseDate(data.entry_date);
    this.exitDate            = FormatDatesService.parseDate(data.exit_date);

    this.avatar              = data.avatar                ? data.avatar                         : null;
    this.avatarImage         = data.avatarImage           ? data.avatarImage                    : null;

    this.lastConnection      = data.last_connection       ? new LoginData(data.last_connection) : null;

    this.tenant              = data.tenant                ? data.tenant                         : null;
    this.phoneNumber         = data.phone_number          ? data.phone_number                   : null;

    this.workLocation        = data.work_location         ? data.work_location                  : null;
    this.dataSources         = data.data_sources          ? data.data_sources                   : null;
    this.altPersonalAccounts = data.alt_personal_accounts ? data.alt_personal_accounts          : null;
  }

  toJSON(): EmployeeDetailsModel {
    return Object.assign(super.toJSON(), {
      created_at:             FormatDatesService.parseDateISO(this.createdAt),
      activated_at:           FormatDatesService.parseDateISO(this.activatedAt),
      last_login_at:          FormatDatesService.parseDateISO(this.lastLoginAt),

      entry_date:             FormatDatesService.parseDateISO(this.entryDate),
      exit_date:              FormatDatesService.parseDateISO(this.exitDate),

      avatar:                this.avatar              ? this.avatar                  : null,
      avatarImage:           this.avatarImage         ? this.avatarImage             : null,

      last_connection:       this.lastConnection      ? this.lastConnection.toJSON() : null,

      tenant:                this.tenant              ? this.tenant                  : null,
      phone_number:          this.phoneNumber         ? this.phoneNumber             : null,
      work_location:         this.workLocation        ? this.workLocation            : null,
      data_sources:          this.dataSources         ? this.dataSources             : null,
      alt_personal_accounts: this.altPersonalAccounts ? this.altPersonalAccounts     : null,
    });
  }

}

export class LoginData {
  lastLoginAt:  Date;
  appVersion:   string;
  manufacturer: string;
  model:        string;
  constructor(data: LoginDataModel) {
    this.lastLoginAt  = FormatDatesService.parseDate(data.last_login_at);
    this.appVersion   = data.app_version   ? data.app_version   : null;
    this.manufacturer = data.manufacturer  ? data.manufacturer  : null;
    this.model        = data.model         ? data.model         : null;
  }

  toJSON(): LoginDataModel {
    return {
      last_login_at: FormatDatesService.parseDateISO(this.lastLoginAt),
      app_version:   this.appVersion   ? this.appVersion   : null,
      manufacturer:  this.manufacturer ? this.manufacturer : null,
      model:         this.model        ? this.model        : null,
    };
  }

}

export class ExternalEmployee extends Employee { 
  constructor(data) {
    super(data);
  }
}

export class InternalEmployee extends Employee { 
  constructor(data) {
    super(data);
  }
}

export class Customer extends Employee { 
  constructor(data) {
    super(data);
  }
}

export class ExternalEmployeeExtended extends EmployeeBasic {
  external_location: number;
  assignments:       AssignmentBasicConfirmed[];
  constructor(data: ExternalEmployeeExtendedModel) {
    super(data);
    this.external_location = data.external_location;
    this.assignments       = data.assignments?.map(a => new AssignmentBasicConfirmed(a));
  }
  
  toJSON(): EmployeeModel {
    return Object.assign(super.toJSON(), {
      email:             null,
      external_location: this.external_location,
      phone_number:      null
    });
  }
}

export class CustomerEmailListItem extends Customer {
  active: boolean;
  constructor(data: EmployeeModel | CustomerEmailListItem) {
    super(data);
    this.active = (data as CustomerEmailListItem).active || false;
  }
}

export class Participant {
  id:                  number;
  userId:              number;
  fullName:            string;
  initials:            string;

  addedById:           number;

  deletedAt:           Date;
  isOnline:            boolean;

  isOwner:             boolean;
  unreadMessagesCount: number;
  constructor(data: ParticipantModel) {
    this.id                  = data.id                    ? data.id                    : null;
    this.userId              = data.am_user_id            ? data.am_user_id            : null;
    this.fullName            = data.full_name             ? data.full_name             : null;
    this.initials            = this.fullName?.split(' ')?.filter(n => n)?.slice(0, 2)?.map(n => n[0])?.join('')?.toUpperCase();

    this.addedById           = data.added_by_id           ? data.added_by_id           : null;

    this.deletedAt           = FormatDatesService.parseDate(data.deleted_at);
    this.isOnline            = data.is_online             ? data.is_online             : null;

    this.isOwner             = data.is_owner              ? data.is_owner              : null;
    this.unreadMessagesCount = data.unread_messages_count ? data.unread_messages_count : null;
  }

  toJSON(): ParticipantModel {
    return {
      id:                    this.id                  ? this.id                    : null,
      am_user_id:            this.userId              ? this.userId                : null,
      full_name:             this.fullName            ? this.fullName              : null,

      added_by_id:           this.addedById           ? this.addedById             : null,

      deleted_at:            FormatDatesService.parseDateISO(this.deletedAt),
      is_online:             this.isOnline            ? this.isOnline              : null,

      is_owner:              this.isOwner             ? this.isOwner               : null,
      unread_messages_count: this.unreadMessagesCount ? this.unreadMessagesCount   : null,
    };
  }

}
